/*
Camera helper for the PWA capture flow.

Implementation note for iOS Safari:
- The cleanest path is a hidden <input type="file" accept="image/*" capture="environment">
  click()-ed from within a user gesture. iOS opens the native camera UI directly.
- getUserMedia() works in standalone PWAs since iOS 14.3 but the UX (full-screen
  custom shutter) is heavier and not what this skeleton ships.
- The captured Blob lands in IndexedDB under the 'photos' store, keyed by a
  generated UUID filename — the same naming scheme the Android app uses.
*/

const kAcceptedMime: string = 'image/*'  // accept anything image, env capture below picks the back camera
const kCaptureFacing: 'environment' | 'user' = 'environment'  // back camera for tree shots

export interface CapturedPhoto
{
	filename: string
	blob: Blob
	takenAt: number
}

/*
Open the device camera and resolve with the captured photo, or reject if the
user dismissed the chooser. The hidden input is created on the fly and
discarded after the event fires.
*/
export function capturePhoto(): Promise<CapturedPhoto>
{
	return new Promise<CapturedPhoto>((inResolve, inReject) => {
		const vInput = document.createElement('input')  // ephemeral file picker
		vInput.type = 'file'
		vInput.accept = kAcceptedMime
		vInput.setAttribute('capture', kCaptureFacing)
		vInput.style.display = 'none'

		vInput.addEventListener('change', () => {
			const vFile = vInput.files?.[0]  // first (and only) selected file
			document.body.removeChild(vInput)
			if (!vFile)
			{
				inReject(new Error('No photo selected'))
				return
			}
			inResolve({
				filename: `${crypto.randomUUID()}.jpg`,
				blob: vFile,
				takenAt: Date.now(),
			})
		}, { once: true })

		vInput.addEventListener('cancel', () => {
			document.body.removeChild(vInput)
			inReject(new Error('Capture cancelled'))
		}, { once: true })

		document.body.appendChild(vInput)
		vInput.click()
	})
}
